/**
 * Chatwoot live-chat widget integration.
 *
 * The SDK script is injected once at boot (main.tsx). Identity, custom
 * attributes, and locale are synced from useChatwootSync. Calls made before
 * the widget fires `chatwoot:ready` are kept as latest-wins pending state and
 * replayed on ready, so login can finish while the script is still loading.
 */
export type ChatwootUser = {
  name?: string;
  email?: string;
  phone_number?: string;
  avatar_url?: string;
  identifier_hash?: string;
};

export type ChatwootCustomAttributes = Record<string, string | number>;

type ChatwootSdk = {
  setUser: (identifier: string, user: ChatwootUser) => void;
  setCustomAttributes: (attrs: ChatwootCustomAttributes) => void;
  setLocale: (locale: string) => void;
  reset: () => void;
  toggle: (state?: 'open' | 'close') => void;
  toggleBubbleVisibility: (state: 'show' | 'hide') => void;
};

declare global {
  interface Window {
    $chatwoot?: ChatwootSdk;
    chatwootSDK?: {
      run: (opts: { websiteToken: string; baseUrl: string }) => void;
    };
    chatwootSettings?: Record<string, unknown>;
  }
}

// Locales shipped by the Chatwoot widget. km/lo/my have no widget
// translations upstream, so they fall back to English.
const WIDGET_LOCALES: Record<string, string> = {
  en: 'en',
  th: 'th',
  vi: 'vi',
  zh: 'zh_CN',
};

export function widgetLocale(appLocale: string): string {
  return WIDGET_LOCALES[appLocale] ?? 'en';
}

let injected = false;
let ready = false;
let cfg: { baseUrl: string; websiteToken: string } | null = null;
let pendingUser: { identifier: string; user: ChatwootUser } | null = null;
let pendingAttrs: ChatwootCustomAttributes | null = null;
let pendingLocale: string | null = null;
let pendingBubble: 'show' | 'hide' | null = null;
let pendingOpen = false;

function enabled(): boolean {
  return Boolean(cfg);
}

/** Test-only: reset the module-level injected/config singleton between cases. */
export function __resetChatwoot(): void {
  injected = false;
  cfg = null;
}

function onReady() {
  ready = true;
  const cw = window.$chatwoot;
  if (!cw) return;
  if (pendingLocale) cw.setLocale(pendingLocale);
  if (pendingUser) cw.setUser(pendingUser.identifier, pendingUser.user);
  if (pendingAttrs) cw.setCustomAttributes(pendingAttrs);
  if (pendingBubble) cw.toggleBubbleVisibility(pendingBubble);
  if (pendingOpen) cw.toggle('open');
  pendingLocale = null;
  pendingUser = null;
  pendingAttrs = null;
  pendingBubble = null;
  pendingOpen = false;
}

export function initChatwoot(
  config: { baseUrl: string; websiteToken: string } | null,
  appLocale: string,
) {
  // baseUrl empty → the SDK src would be a relative /packs/js/sdk.js, which the
  // SPA catch-all answers with index.html (MIME error). Skip until a tenant
  // configures a real self-hosted Chatwoot origin.
  if (!config || !config.baseUrl.trim() || !config.websiteToken.trim()) return;
  if (injected || typeof document === 'undefined') return;
  cfg = config;
  injected = true;

  window.chatwootSettings = {
    position: 'right',
    type: 'standard',
    locale: widgetLocale(appLocale),
    darkMode: 'auto',
    hideMessageBubble: true,
  };
  window.addEventListener('chatwoot:ready', onReady, { once: true });

  const script = document.createElement('script');
  script.src = `${config.baseUrl}/packs/js/sdk.js`;
  script.async = true;
  script.onload = () => {
    window.chatwootSDK?.run({ websiteToken: config.websiteToken, baseUrl: config.baseUrl });
  };
  document.head.appendChild(script);
}

export function setChatwootUser(
  identifier: string,
  user: ChatwootUser,
  attrs?: ChatwootCustomAttributes,
) {
  if (!enabled()) return;
  if (ready && window.$chatwoot) {
    window.$chatwoot.setUser(identifier, user);
    if (attrs) window.$chatwoot.setCustomAttributes(attrs);
    return;
  }
  pendingUser = { identifier, user };
  if (attrs) pendingAttrs = attrs;
}

export function setChatwootLocale(appLocale: string) {
  if (!enabled()) return;
  const locale = widgetLocale(appLocale);
  if (ready && window.$chatwoot) {
    window.$chatwoot.setLocale(locale);
    return;
  }
  pendingLocale = locale;
}

/** Clears the widget session + contact identity. Call on logout. */
export function resetChatwoot() {
  if (!enabled()) return;
  pendingUser = null;
  pendingAttrs = null;
  if (ready && window.$chatwoot) window.$chatwoot.reset();
}

/** Open the chat panel (Support page CTA). Queued if the widget is still loading. */
export function openChatwootChat() {
  if (!enabled()) return;
  if (ready && window.$chatwoot) {
    window.$chatwoot.toggle('open');
    return;
  }
  pendingOpen = true;
}

/** Hide the launcher while a game surface covers the screen. */
export function toggleChatwootBubble(state: 'show' | 'hide') {
  if (!enabled()) return;
  if (ready && window.$chatwoot) {
    window.$chatwoot.toggleBubbleVisibility(state);
    return;
  }
  pendingBubble = state;
}
